import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { getBulletinList } from '../../Utils/frontStageApi';
import { BulletinTag } from '../../Utils/BulletinTag';
import { BulletinActionBar } from '../../Components/Bulletin/BulletinActionBar';
import { BulletinTagFilter } from '../../Components/Bulletin/BulletinTagFilter';
import { BulletinManagerTable, BulletinManagerItem } from '../../Components/Bulletin/BulletinManagerTable';

const FILTER_TAGS: BulletinTag[] = ['系統維護', '最新消息', '新功能上線'];

const parseDate = (d: string): number => {
  const n = new Date(d.replace(/\./g, '/').replace(/-/g, '/').trim()).getTime();
  return Number.isNaN(n) ? 0 : n;
};

const BulletinManagerPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [bulletins, setBulletins] = useState<BulletinManagerItem[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<BulletinTag[]>(FILTER_TAGS);
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');

  useEffect(() => {
    getBulletinList(0, 1).then((data) => {
      if (!data?.list) return;
      setBulletins(
        data.list.map((item) => ({
          bid: item.bid,
          date: item.date,
          title: item.title,
          tag: item.tag as BulletinTag,
        })),
      );
    });
  }, []);

  const handleTagToggle = (tag: BulletinTag) =>
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const filtered = useMemo(() => {
    const f =
      selectedTags.length === 0
        ? []
        : bulletins
            .filter((i) => selectedTags.includes(i.tag))
            .filter((i) => searchText.trim() === '' || i.title.includes(searchText.trim()));
    return [...f].sort((a, b) => {
      const diff = parseDate(a.date) - parseDate(b.date);
      return sortDirection === 'desc' ? -diff : diff;
    });
  }, [bulletins, selectedTags, searchText, sortDirection]);

  return (
    <Flex w="100%" direction="column" align="center" pt="50px" pb="100px">
      <Text
        fontFamily="'Open Sans', sans-serif"
        fontSize="30px"
        fontWeight={600}
        lineHeight="133%"
        letterSpacing="0.01em"
        color="#000000"
        textAlign="center"
      >
        {t('bulletin.pageTitle')}
      </Text>

      <BulletinActionBar
        mt="50px"
        searchText={searchText}
        onSearchChange={setSearchText}
        onAdd={() => history.push('/bs/bulletin/create')}
      />

      <BulletinTagFilter mt="22px" selectedTags={selectedTags} onTagToggle={handleTagToggle} />

      <BulletinManagerTable
        mt="22px"
        bulletins={filtered}
        sortDirection={sortDirection}
        onSortToggle={() => setSortDirection((p) => (p === 'desc' ? 'asc' : 'desc'))}
      />
    </Flex>
  );
};

export default BulletinManagerPage;
